/**
 * Battle Menu overlay — in-battle settings panel.
 * Shown when the ⚙ button in the top-bar is clicked during battle.
 * Actions: toggle audio, toggle language, resume, restart battle, exit to map.
 * The opener is responsible for pausing / resuming the game loop around open/close.
 */

import type { GameStateView } from "../../application/ports.js";
import { getLocale, t } from "../../i18n/index.js";

export interface BattleMenuCallbacks {
  onOpen?: () => void;
  onClose?: () => void;
  onToggleAudio?: () => void;
  onToggleLocale?: () => void;
  onExitToMap?: () => void;
  onRestart?: () => void;
  isAudioMuted?: () => boolean;
}

export interface BattleMenu {
  open(): void;
  close(): void;
  isOpen(): boolean;
  update(state: GameStateView): void;
  destroy(): void;
}

export function createBattleMenuOverlay(
  container: HTMLElement,
  cbs: BattleMenuCallbacks
): BattleMenu {
  let open = false;

  const root = document.createElement("div");
  root.className = "hud-battle-menu";
  root.style.display = "none";
  container.appendChild(root);

  const backdrop = document.createElement("div");
  backdrop.className = "hud-battle-menu__backdrop";
  root.appendChild(backdrop);

  const panel = document.createElement("div");
  panel.className = "hud-battle-menu__panel hud-panel";
  root.appendChild(panel);

  const title = document.createElement("div");
  title.className = "hud-battle-menu__title";
  panel.appendChild(title);

  const list = document.createElement("div");
  list.className = "hud-battle-menu__list";
  panel.appendChild(list);

  const audioBtn = makeBtn();
  const localeBtn = makeBtn();
  const resumeBtn = makeBtn("primary");
  const restartBtn = makeBtn();
  const exitBtn = makeBtn();
  list.appendChild(audioBtn);
  list.appendChild(localeBtn);
  list.appendChild(resumeBtn);
  list.appendChild(restartBtn);
  list.appendChild(exitBtn);

  backdrop.addEventListener("click", () => doClose());
  audioBtn.addEventListener("click", () => {
    cbs.onToggleAudio?.();
    refresh();
  });
  localeBtn.addEventListener("click", () => {
    cbs.onToggleLocale?.();
    refresh();
  });
  resumeBtn.addEventListener("click", () => doClose());
  restartBtn.addEventListener("click", () => {
    closeSilently();
    cbs.onRestart?.();
  });
  exitBtn.addEventListener("click", () => {
    closeSilently();
    cbs.onExitToMap?.();
  });

  const keydownHandler = (e: KeyboardEvent): void => {
    if (open && e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      doClose();
    }
  };
  document.addEventListener("keydown", keydownHandler, true);

  function makeBtn(variant?: "primary"): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.className =
      "hud-battle-menu__btn" +
      (variant ? " hud-battle-menu__btn--" + variant : "");
    return b;
  }

  function refresh(): void {
    title.textContent = t("battleMenu.title");
    const muted = cbs.isAudioMuted?.() ?? false;
    audioBtn.textContent = muted
      ? t("battleMenu.audioOff")
      : t("battleMenu.audioOn");
    localeBtn.textContent =
      getLocale() === "ru" ? "Language: EN" : "Язык: RU";
    resumeBtn.textContent = t("battleMenu.resume");
    restartBtn.textContent = t("battleMenu.restart");
    exitBtn.textContent = t("battleMenu.exit");
  }

  function doOpen(): void {
    if (open) return;
    open = true;
    refresh();
    root.style.display = "";
    cbs.onOpen?.();
  }

  function doClose(): void {
    if (!open) return;
    open = false;
    root.style.display = "none";
    cbs.onClose?.();
  }

  function closeSilently(): void {
    if (!open) return;
    open = false;
    root.style.display = "none";
  }

  return {
    open: doOpen,
    close: doClose,
    isOpen: () => open,
    update(state: GameStateView): void {
      if (open && state.scene !== "game") {
        closeSilently();
        return;
      }
      if (open) refresh();
    },
    destroy(): void {
      document.removeEventListener("keydown", keydownHandler, true);
      if (root.parentElement) root.parentElement.removeChild(root);
    },
  };
}
